import { addGuard, wait } from "@/utils";
import { rpcEmitter } from "@/modules/events/emitter";
import { messages, RPCRequests } from "@/modules/communication";
import Xmpp from "@/modules/Xmpp";
import RPC from "@/modules/RPC";
import { getHasPermissionError } from "@/modules/stream/permissions";
import { getMediaStream } from "@/modules/stream/getMediaStream";
import Peers from "@/modules/Peers";
import Messenger from "@/modules/Messenger";
import PresenceKeeper from "@/modules/app/Presence";
import AudioPlayer from "camera/lib/Media/AudioPlayer";
import CameraRtcManager from "camera/modules/CameraRtcManager";
import { cameraStore } from "camera/store/camera";
import { stationStore } from "camera/store/station";
import { useApp } from "common/store/app";
import streamQualityUpdater from "../streamQualityUpdater";
import { getCameraAndMicrophoneInfo, createDeviceChangeHandler } from "../media-devices";
import Notifier from "../Notifier";
import { stopFaceIfrunning } from "./utils";
import RpcEventUpdater from "./RpcEventUpdater";
import Handlers from "./rpcHandlers";
import ManualVideoManager from "./ManualVideoManager";
import EventManager from "camera/modules/events/EventManager";
import { usePendingEvents } from "camera/store/pendingEvents";
import environment from "@/modules/environment";

type Providing = {
  changeCamera: MonitoringManager["changeCamera"];
  stopFace: MonitoringManager["stopFace"];
  changeCameraStationRecordingMode: MonitoringManager["changeCameraStationRecordingMode"];
};

const {
  setError,
  setCameraList,
  setCameraId,
  setMicrophoneId,
  updateTrack,
  setActiveFaceJid,
  setStream,
  destroyTracks
} = cameraStore();
const { setRecordingMode } = stationStore();
const {
  StartMonitoring,
  StopMonitoring,
  Ping,
  StartVideoBroadcasting,
  ChangeCamera,
  StartVideoReceiving,
  StopVideoReceiving,
  StartCameraHistoryReplayEvents,
  ReloadCameraHistoryReplayEvents,
  StopCameraHistoryReplayEvents,
  SyncMonitoring,
  ChangeCameraStationRecordingMode,
  SetCameraAlarm,
  GetDetectionZonesPreview,
  SetCameraVideoRecording
} = RPCRequests;
const handlers = new Handlers();

export default class MonitoringManager {
  constructor(private stationManager: CameraStationManager) {
    this.providing = {
      changeCamera: this.changeCamera,
      stopFace: this.stopFace,
      changeCameraStationRecordingMode: this.changeCameraStationRecordingMode
    };
  }
  providing = {} as Providing;
  private notifier = new Notifier();
  private xmpp = Xmpp.getInstance();
  private messenger = Messenger.getInstance();
  private rtcManager = new CameraRtcManager();
  private rpc = new RPC();
  // better name?
  private rpcEventUpdater = new RpcEventUpdater(this.messenger);
  private manualVideoManager = new ManualVideoManager();

  init = async () => {
    handlers.init({
      stationManager: this.stationManager,
      rtcManager: this.rtcManager,
      manualVideoManager: this.manualVideoManager,
      changeCamera: this.changeCamera,
      rpcEventUpdater: this.rpcEventUpdater,
      changeCameraStationRecordingMode: this.changeCameraStationRecordingMode,
      setCameraAlarm: this.setCameraAlarm,
      setCameraVideoRecording: this.setCameraVideoRecording
    });
    this.rpcEventUpdater.init();

    this.rtcManager.startListening();
    this.rpc.startListening();
    PresenceKeeper.setDefaults();

    this.stationManager.on("battery-change", this.batteryChange);
    this.manualVideoManager.on("event-end", this.manualVideoEnd);

    rpcEmitter.on(StartMonitoring.name, handlers.startMonitoring);
    rpcEmitter.on(StopMonitoring.name, handlers.stopMonitoring);
    rpcEmitter.on(StartVideoBroadcasting.name, handlers.startVideoBroadcast);
    rpcEmitter.on(Ping.name, handlers.ping);
    rpcEmitter.on(ChangeCamera.name, handlers.changeCamera);
    rpcEmitter.on(StartVideoReceiving.name, handlers.startVideoReceiving);
    rpcEmitter.on(StopVideoReceiving.name, handlers.stopVideoReceiving);
    rpcEmitter.on(StartCameraHistoryReplayEvents.name, handlers.startCameraHistoryReplayEvents);
    rpcEmitter.on(ReloadCameraHistoryReplayEvents.name, handlers.reloadCameraHistoryReplayEvents);
    rpcEmitter.on(StopCameraHistoryReplayEvents.name, handlers.stopCameraHistoryReplayEvents);
    rpcEmitter.on(SyncMonitoring.name, handlers.syncMonitoring);
    rpcEmitter.on(ChangeCameraStationRecordingMode.name, handlers.changeCameraStationRecordingMode);
    rpcEmitter.on(SetCameraAlarm.name, handlers.setCameraAlarm);
    rpcEmitter.on(GetDetectionZonesPreview.name, handlers.getDetectionZonesPreview);
    rpcEmitter.on(SetCameraVideoRecording.name, handlers.setCameraVideoRecording);
    let deviceInfo = await getCameraAndMicrophoneInfo();

    if (deviceInfo) {
      setMicrophoneId(deviceInfo.microphoneId);
      setCameraId(deviceInfo.cameraId);
      setCameraList(deviceInfo.cameraList);
    }

    try {
      const canRecord = stationStore().recordingMode === "ON";

      const mediaStream = await addGuard(() =>
        getMediaStream({
          audio: { deviceId: deviceInfo?.microphoneId },
          video: { deviceId: deviceInfo?.cameraId }
        })
      );
      if ("error" in mediaStream) {
        if (getHasPermissionError(mediaStream.error)) setError("permission_denied");
        else setError("failure");
        return;
      }
      const { result: stream } = mediaStream;
      const hasAudio = stream.getAudioTracks().length > 0;
      const hasVideo = stream.getVideoTracks().length > 0;

      if (hasAudio && hasVideo) {
        if (canRecord) await streamQualityUpdater.updateVideoTrackQuality(stream);
        setStream(stream);
      } else {
        if (!hasAudio && !hasVideo) setError("failure");
        else if (!hasAudio) setError("no_audio");
        else if (!hasVideo) setError("no_video");
      }
      if (!canRecord) destroyTracks();

      // Safari workaround to fix missing device IDs after first getUserMedia() call
      const isSafari = environment.system.getBrowserName() === "Safari";
      if (isSafari) {
        const newDeviceInfo = await getCameraAndMicrophoneInfo();
        if (newDeviceInfo) {
          deviceInfo = newDeviceInfo;
          if (newDeviceInfo.microphoneId) setMicrophoneId(newDeviceInfo.microphoneId);
          if (newDeviceInfo.cameraId) setCameraId(newDeviceInfo.cameraId);
          if (newDeviceInfo.cameraList) setCameraList(newDeviceInfo.cameraList);
        }
      }

      this.xmpp.sendPresence(canRecord ? "sender" : "sender_off", await this.stationManager.getCurrentStatePayload());
    } catch (err: any) {
      log.err("Failed 'init' in MonitoringManager", err);
      useApp.getState().setStatus("FAILURE");
    } finally {
      createDeviceChangeHandler(deviceInfo?.audioOutputs);
    }
  };

  changeCamera = async (cameraId: string, jidToIgnore?: string) => {
    const stream = await getMediaStream({
      video: { deviceId: cameraId },
      audio: false
    });
    const videoTrack = stream.getVideoTracks()[0];
    updateTrack(videoTrack);
    setCameraId(cameraId);

    const message = messages.CameraAndFlashlightState.create({ cameraId });
    this.notifier.sendMessageToConnectedViewers(message, jidToIgnore);
  };

  stopFace = async () => {
    const to = cameraStore().activeFaceJid;
    if (!to) return;

    const message = messages.StopVideoBroadcasting.create();
    this.messenger.sendAsEnvelope({
      to,
      payload: { message }
    });
    setActiveFaceJid(null);
  };

  batteryChange = (batteryStatus: BatteryStatus) => {
    const message = messages.BatteryStatus.create(batteryStatus);
    this.notifier.sendMessageToConnectedViewers(message);
  };

  manualVideoEnd = (event: CameraEvent) => {
    const message = messages.CameraVideoRecordingStatus.create({ recordingOff: { event } });
    this.notifier.sendMessageToConnectedViewers(message);
  };

  changeCameraStationRecordingMode = async (mode: CameraStationRecordingMode, jidToIgnore?: string) => {
    if (mode === "ON") {
      const existingStream = cameraStore().stream;
      if (existingStream) {
        const stream = await getMediaStream({
          audio: { deviceId: cameraStore()?.microphoneId || undefined },
          video: { deviceId: cameraStore()?.cameraId || undefined }
        });
        stream.getTracks().forEach(updateTrack);
      }
      await wait(500);
      this.xmpp.sendPresence("sender");
      setRecordingMode(mode);
    }
    if (mode === "OFF") {
      const lastSegmentEnd = usePendingEvents.getState().lastUploadedSegmentEndTime;
      EventManager.setEndTimeOverride(lastSegmentEnd);
      setRecordingMode(mode);

      const request = RPCRequests.StopMonitoringFromCameraStation.create();
      const peers = Peers.getAllAsArray().filter((peer) => peer.jid !== jidToIgnore);

      await Promise.all(
        peers.map(async (peer) => {
          this.rtcManager.rtc.destroyPeerConnection(peer.jid);
          stopFaceIfrunning(peer.jid);
          log.info("Sending stopMonitoringFromCameraStation request to", peer.jid);
          const response = await this.rpc.call(request, peer.jid);
          log.info("Received response from", peer.jid, response);
        })
      );
      destroyTracks();
      this.xmpp.sendPresence("sender_off");
    }
  };

  setCameraAlarm = async (state: AlarmState, jidToIgnore?: string) => {
    if (state === "ALARM_ON") await AudioPlayer.play("/alarm.mp3", { loop: true });
    else AudioPlayer.stop();

    const message = messages.CameraAlarmStatus.create({ state });
    this.notifier.sendMessageToConnectedViewers(message, jidToIgnore);
  };

  setCameraVideoRecording = async (state: CameraRecordingState, jidToIgnore?: string) => {
    if (state === "RECORDING_ON") this.manualVideoManager.startManualVideoEvent();
    else this.manualVideoManager.endManualVideoEvent();

    const status = this.manualVideoManager.getStatus();
    const message = messages.CameraVideoRecordingStatus.create(status);
    this.notifier.sendMessageToConnectedViewers(message, jidToIgnore);
    return status;
  };

  // destroy = () => {
  //   log.info("destroy");
  //   this.stationManager.off("battery-change", this.batteryChange);
  //   rpcEmitter.off(StartMonitoring.name, handlers.startMonitoring);
  //   rpcEmitter.off(StopMonitoring.name, handlers.stopMonitoring);
  //   rpcEmitter.off(StartVideoBroadcasting.name, handlers.startVideoBroadcast);
  //   rpcEmitter.off(Ping.name, handlers.ping);
  //   rpcEmitter.off(ChangeCamera.name, handlers.changeCamera);
  //   rpcEmitter.off(StartVideoReceiving.name, handlers.startVideoReceiving);
  //   rpcEmitter.off(StopVideoReceiving.name, handlers.stopVideoReceiving);
  //   rpcEmitter.off(
  //     StartCameraHistoryReplayEvents.name,
  //     handlers.startCameraHistoryReplayEvents
  //   );
  //   rpcEmitter.off(
  //     ReloadCameraHistoryReplayEvents.name,
  //     handlers.reloadCameraHistoryReplayEvents
  //   );
  //   rpcEmitter.off(
  //     StopCameraHistoryReplayEvents.name,
  //     handlers.stopCameraHistoryReplayEvents
  //   );
  //   this.rtcManager.destroy();
  //   this.rpc.destroy();
  //   this.xmpp.sendPresence("not_chosen_yet");
  // };
}
